// Copy-paste embed snippets for a niche.
//
// THE thing a marketer/dev actually pastes onto a lander: two <script> + <div>
// pairs (consent + footer). No host/brand/niche is configured anywhere — the
// niche is the `/<slug>/` in the script URL, and the brand auto-derives from the
// embedding page's own window.location.hostname. This panel just renders the
// exact snippets for THIS niche so nobody has to read the PRP or guess.
//
// Contract (verified against the deployed widget loader):
//   consent: <script src="<host>/w/<slug>/consent.js" defer></script>
//            + <div data-loop-legal="consent"></div>  (INSIDE the host form)
//   footer:  <script src="<host>/w/<slug>/footer.js" defer></script>
//            + <div data-loop-legal="footer"></div>

import { useState } from "react";

import { widgetHost } from "../lib/legalApi";
import { buttonStyle, Card, tokens } from "../lib/theme";

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      style={{ ...buttonStyle("ghost"), fontSize: 12, padding: "4px 10px" }}
      onClick={() => {
        // navigator.clipboard is available on the https admin origin.
        void navigator.clipboard
          .writeText(text)
          .then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          })
          .catch(() => {
            /* clipboard blocked — the operator can still select the text */
          });
      }}
      aria-label="Copy snippet to clipboard"
    >
      {copied ? "Copied ✓" : "Copy"}
    </button>
  );
}

function Snippet({ title, hint, code }: { title: string; hint: string; code: string }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 6,
        }}
      >
        <strong style={{ fontSize: 14 }}>{title}</strong>
        <CopyButton text={code} />
      </div>
      <p style={{ margin: "0 0 6px", fontSize: 12, color: tokens.color.fg1 }}>
        {hint}
      </p>
      <pre
        style={{
          margin: 0,
          padding: "10px 12px",
          background: tokens.color.bg3,
          border: `1px solid ${tokens.color.border}`,
          borderRadius: 8,
          overflowX: "auto",
          fontSize: 12.5,
          lineHeight: 1.5,
          whiteSpace: "pre",
        }}
      >
        <code>{code}</code>
      </pre>
    </div>
  );
}

export default function EmbedSnippets({ slug }: { slug: string }) {
  // widgetHost() throws if VITE_LEGAL_API_BASE is unset; guard so the editor
  // still renders (with a clear note) rather than crashing the page.
  let host: string | null = null;
  let hostError: string | null = null;
  try {
    host = widgetHost();
  } catch (e) {
    hostError = e instanceof Error ? e.message : String(e);
  }

  if (!host) {
    return (
      <Card>
        <h2 style={{ marginTop: 0, fontSize: 16 }}>Embed snippets</h2>
        <p style={{ color: tokens.color.danger, fontSize: 13 }}>
          Can’t build snippets: {hostError}. Set VITE_LEGAL_API_BASE.
        </p>
      </Card>
    );
  }

  const consent = `<!-- Consent block — place this <div> INSIDE your lead <form data-tf-element-role="offer"> -->
<div data-loop-legal="consent"></div>
<script src="${host}/w/${slug}/consent.js" defer></script>`;

  const footer = `<!-- Footer — place near the bottom of the page -->
<div data-loop-legal="footer"></div>
<script src="${host}/w/${slug}/footer.js" defer></script>`;

  return (
    <Card>
      <h2 style={{ marginTop: 0, fontSize: 16 }}>Embed snippets</h2>
      <p style={{ margin: "0 0 14px", fontSize: 13, color: tokens.color.fg1 }}>
        Paste these onto any <code>{slug}</code> landing page — identical on every
        site. No host, brand, or niche is configured: the brand auto-derives from
        the page’s own domain, and the niche is the <code>/{slug}/</code> in the
        URL. Publish your edits here and every embedded page updates.
      </p>

      <Snippet
        title="1 · Consent block"
        hint="Goes INSIDE the lead form (so TrustedForm certifies the consent text)."
        code={consent}
      />
      <Snippet
        title="2 · Footer"
        hint="Goes anywhere near the bottom of the page."
        code={footer}
      />

      <p style={{ margin: "6px 0 0", fontSize: 12, color: tokens.color.fg1 }}>
        Keep the page’s own TrustedForm Certify snippet (with
        <code> use_tagged_consent=true</code>) before <code>&lt;/body&gt;</code>.
      </p>
    </Card>
  );
}
